// ============================================
// tradeBuffer.js
// Columnar (Structure-of-Arrays) raw-trade buffer.
//
// WHY THIS EXISTS
// ---------------
// The raw swap buffer is the single largest live data structure the chart
// holds: up to the retention window (3h) of every trade on a busy pair —
// hundreds of thousands of records. Held as an Array of plain objects
// ({time, side, amount, tradeMcap}) that is ~80–100 bytes and one GC-tracked
// allocation PER TRADE. On a session that never rotates tokens the object
// count climbs into six figures and the garbage collector has to walk every
// one of them on each major cycle — the heap/GC cost that built into the
// session-long lag a refresh cleared.
//
// This buffer stores the SAME data as four contiguous typed arrays (columns)
// instead of one array of objects:
//
//     _time    Float64Array   ms epoch
//     _mcap    Float64Array   trade market cap (SOL)
//     _amount  Float64Array   size (SOL notional)   ← Float64, not Float32,
//                                                     so persisted values are
//                                                     bit-identical to the
//                                                     object path and dedup
//                                                     keys never diverge.
//     _side    Int8Array      +1 buy / -1 sell
//
// ~25 bytes/trade and ZERO per-trade GC objects for the durable store. A full
// buffer rescan (timeframe rebuild, fine re-seed, persistence) reads columns
// with no object materialization at all.
//
// THE Array.isArray CONSTRAINT
// ----------------------------
// The strategy consumers (OrderflowBot, ArdDetector, BundleDetector) are fed
// this buffer at ~4Hz and are hard-wired to a real Array: ArdDetector guards
// `if (!Array.isArray(swaps)) return`, OrderflowBot does `.slice()`, indexing,
// `.length`, and HOLDS the reference across ticks. A flyweight/Proxy fails
// Array.isArray, so a real array is mandatory at that boundary.
//
// Resolution: the columns are the durable store (full retention); a SEPARATE,
// bounded, incrementally-maintained real Array of objects — the "consumer
// view" — mirrors only the most recent VIEW_WINDOW_MS of trades (comfortably
// larger than the widest consumer lookback, ArdDetector's 900s profile). Its
// identity is stable (mutated in place, never reassigned) so a consumer that
// stashes the ref keeps seeing updates, exactly like the old shared array did.
// Objects are allocated one-per-trade on push (same as before) but now age out
// of the view after VIEW_WINDOW_MS instead of living for the full 3h — the
// live object set shrinks ~9× and the columns carry the long tail for free.
//
// The interleaved Float64 layout [t, mcap, amount, sign] emitted by toFloat64()
// is byte-for-byte the format candleStore already persists, so writing to disk
// is a straight copy with no marshalling.
// ============================================

const BUY = 1
const SELL = -1

// Consumer view retention. Must exceed the widest lookback any consumer scans
// so the bounded view is always a superset of what they need:
//   ArdDetector.profileWindowSec  = 900s  (15min)  ← the widest
//   OrderflowBot churn/base baseline = 300s (5min)
// 20min leaves a 5min margin. Consumers apply their own Date.now()-based
// cutoffs on top, so an extra straggler in the view is harmless; this only
// bounds how far back the view is guaranteed to reach.
const VIEW_WINDOW_MS = 20 * 60 * 1000

const MIN_CAPACITY = 1024

export class TradeBuffer {
  constructor(opts = {}) {
    const cap = Math.max(MIN_CAPACITY, (opts.capacity | 0) || 0)
    this._cap = cap
    this._len = 0
    this._time = new Float64Array(cap)
    this._mcap = new Float64Array(cap)
    this._amount = new Float64Array(cap)
    this._side = new Int8Array(cap)
    // Wallet id, interned by the caller (0 = unknown/unattributed). Uint32
    // rather than the address string: 4 bytes per trade instead of ~44, and
    // it keeps the whole record columnar so nothing about the Float64
    // packing changes. Backfill rows carry no trader, so history is legitimately
    // 0 — absence of attribution, not attribution to nobody.
    this._wallet = new Uint32Array(cap)

    this._viewWindowMs = opts.viewWindowMs > 0 ? opts.viewWindowMs : VIEW_WINDOW_MS
    // Stable consumer-facing Array. NEVER reassigned — only mutated in place
    // (push / splice / length=0) so a consumer holding the reference across
    // ticks always observes the current state.
    this._view = []
    this._maxTime = -Infinity
  }

  get length() { return this._len }

  // ── capacity ─────────────────────────────────────────────
  _ensure(extra) {
    const need = this._len + extra
    if (need <= this._cap) return
    let cap = this._cap || MIN_CAPACITY
    while (cap < need) cap *= 2
    const time = new Float64Array(cap); time.set(this._time.subarray(0, this._len)); this._time = time
    const mcap = new Float64Array(cap); mcap.set(this._mcap.subarray(0, this._len)); this._mcap = mcap
    const amount = new Float64Array(cap); amount.set(this._amount.subarray(0, this._len)); this._amount = amount
    const side = new Int8Array(cap); side.set(this._side.subarray(0, this._len)); this._side = side
    const wal = new Uint32Array(cap); wal.set(this._wallet.subarray(0, this._len)); this._wallet = wal
    this._cap = cap
  }

  // ── append (hot path — one per live trade) ───────────────
  push(time, side, amount, tradeMcap, walletId = 0) {
    this._ensure(1)
    const i = this._len++
    this._time[i] = time
    this._mcap[i] = tradeMcap
    this._amount[i] = amount
    this._side[i] = side === 'buy' ? BUY : SELL
    this._wallet[i] = walletId | 0
    if (time > this._maxTime) this._maxTime = time
    // Mirror into the bounded consumer view (append only; stale entries are
    // evicted lazily in consumerView so the hot path stays O(1)).
    this._view.push({ time, side, amount, tradeMcap, walletId })
  }

  // ── time trim (candle close) ─────────────────────────────
  // Keep every trade with time >= cutoff. Order-INDEPENDENT compaction —
  // exactly matches the old `.filter(t => t.time >= cutoff)` and is robust to
  // out-of-order live trades (a stale binary-search boundary would not be).
  trimBefore(cutoff) {
    if (this._len === 0) return
    let w = 0
    for (let r = 0; r < this._len; r++) {
      if (this._time[r] >= cutoff) {
        if (w !== r) {
          this._time[w] = this._time[r]
          this._mcap[w] = this._mcap[r]
          this._amount[w] = this._amount[r]
          this._side[w] = this._side[r]
          this._wallet[w] = this._wallet[r]
        }
        w++
      }
    }
    if (w !== this._len) {
      this._len = w
      this._rebuildView()
    }
  }

  // ── prepend older history (backfill / disk restore) ──────
  // `rows` = array of {time, tradeMcap, amount, side} strictly older than the
  // current head, in chronological order. Inserted before index 0.
  prepend(rows) {
    const m = rows.length
    if (m === 0) return
    this._ensure(m)
    // Shift existing block right by m (typed-array memmove).
    this._time.copyWithin(m, 0, this._len)
    this._mcap.copyWithin(m, 0, this._len)
    this._amount.copyWithin(m, 0, this._len)
    this._side.copyWithin(m, 0, this._len)
    this._wallet.copyWithin(m, 0, this._len)
    for (let i = 0; i < m; i++) {
      const r = rows[i]
      this._time[i] = r.time
      this._mcap[i] = r.tradeMcap
      this._amount[i] = r.amount
      this._side[i] = r.side === 'buy' ? BUY : SELL
      this._wallet[i] = r.walletId | 0
    }
    this._len += m
    this._rebuildView()
  }

  // ── wholesale replace (union / re-sort) ──────────────────
  // `rows` = array of {time, tradeMcap, amount, side}, already deduped/sorted.
  replaceAll(rows) {
    const n = rows.length
    this._len = 0
    this._ensure(n)
    for (let i = 0; i < n; i++) {
      const r = rows[i]
      this._time[i] = r.time
      this._mcap[i] = r.tradeMcap
      this._amount[i] = r.amount
      this._side[i] = r.side === 'buy' ? BUY : SELL
      this._wallet[i] = r.walletId | 0
    }
    this._len = n
    this._rebuildView()
  }

  clear() {
    this._len = 0
    this._maxTime = -Infinity
    this._view.length = 0
  }

  // ── columnar reads (zero-alloc) ──────────────────────────
  timeAt(i) { return this._time[i] }
  mcapAt(i) { return this._mcap[i] }
  amountAt(i) { return this._amount[i] }
  sideAt(i) { return this._side[i] > 0 ? 'buy' : 'sell' }
  sideSignAt(i) { return this._side[i] }
  walletAt(i) { return this._wallet[i] }

  get(i) {
    return {
      time: this._time[i],
      side: this._side[i] > 0 ? 'buy' : 'sell',
      amount: this._amount[i],
      tradeMcap: this._mcap[i],
      walletId: this._wallet[i],
    }
  }

  oldestTime() { return this._len ? this._time[0] : Infinity }
  newestTime() { return this._len ? this._maxTime : -Infinity }
  lastMcap() { return this._len ? this._mcap[this._len - 1] : 0 }
  // papernofee-20261027: mcap of the most recent print on one side (0 if none).
  lastMcapOf(side) {
    const want = side === 'buy' ? BUY : SELL
    for (let i = this._len - 1; i >= 0; i--) if (this._side[i] === want) return this._mcap[i]
    return 0
  }

  // Iterate the whole buffer with no per-trade object allocation.
  // cb(time, tradeMcap, side, amount, index)
  forEach(cb) {
    for (let i = 0; i < this._len; i++) {
      cb(this._time[i], this._mcap[i], this._side[i] > 0 ? 'buy' : 'sell', this._amount[i], i)
    }
  }

  // Object-array snapshot (diagnostics / ad-hoc). Accepts optional slice bounds
  // so it can stand in for Array.prototype.slice at the one call site that uses
  // it. Prefer forEach() for hot full scans — this materializes objects.
  toArray() {
    const out = new Array(this._len)
    for (let i = 0; i < this._len; i++) {
      out[i] = {
        time: this._time[i],
        side: this._side[i] > 0 ? 'buy' : 'sell',
        amount: this._amount[i],
        tradeMcap: this._mcap[i],
      }
    }
    return out
  }

  slice(start, end) {
    if (start === undefined && end === undefined) return this.toArray()
    return this.toArray().slice(start, end)
  }

  // Interleaved [time, mcap, amount, sign] × len — the exact on-disk format
  // candleStore persists. Straight copy, no marshalling.
  toFloat64() {
    const out = new Float64Array(this._len * 4)
    for (let i = 0; i < this._len; i++) {
      const o = i * 4
      out[o] = this._time[i]
      out[o + 1] = this._mcap[i]
      out[o + 2] = this._amount[i]
      out[o + 3] = this._side[i]
    }
    return out
  }

  // ── consumer view (real Array for Array.isArray consumers) ──
  // Returns the stable, bounded, most-recent-window Array of trade objects.
  // Evicts entries older than the view window (anchored on the newest trade)
  // before returning. Called at the ~4Hz strategy cadence.
  consumerView() {
    const v = this._view
    if (v.length > 0 && this._maxTime > -Infinity) {
      const cutoff = this._maxTime - this._viewWindowMs
      let k = 0
      while (k < v.length && v[k].time < cutoff) k++
      if (k > 0) v.splice(0, k)
    }
    return v
  }

  // Rebuild the consumer view in place from the recent tail of the columns.
  // Called after any structural mutation (trim/prepend/replaceAll). Keeps the
  // SAME array instance so held consumer references stay valid.
  _rebuildView() {
    let mx = -Infinity
    for (let i = 0; i < this._len; i++) if (this._time[i] > mx) mx = this._time[i]
    this._maxTime = mx
    const cutoff = mx - this._viewWindowMs
    const v = this._view
    v.length = 0
    for (let i = 0; i < this._len; i++) {
      if (this._time[i] >= cutoff) {
        v.push({
          time: this._time[i],
          side: this._side[i] > 0 ? 'buy' : 'sell',
          amount: this._amount[i],
          tradeMcap: this._mcap[i],
          walletId: this._wallet[i],
        })
      }
    }
  }

  // Lightweight heap/debug stats (exposed via window.__cheddarTrades.current).
  stats() {
    return {
      len: this._len,
      cap: this._cap,
      viewLen: this._view.length,
      bytesApprox: this._cap * (8 * 3 + 1),
    }
  }
}

export default TradeBuffer